import { useCallback, useState } from 'react';
import {
  ensureTelegramWebAppSdk,
  looksLikeTelegramRuntime,
  waitForTelegramInitData,
} from '@/telegram';
import { loginWithTelegram, restoreWebAppSession } from './auth.api';
import { detectAuthRuntime } from './runtime';
import { useAuthStore } from './store';
import { clearStoredWebSession, readStoredWebSession } from './web-session-storage';

export type LoginStatus =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready' }
  | { state: 'error'; reason: 'telegram_auth_failed' | 'unknown'; message?: string };

export function useLogin() {
  const setRuntime = useAuthStore((s) => s.setRuntime);
  const setAuth = useAuthStore((s) => s.setAuth);
  const setSignedOut = useAuthStore((s) => s.setSignedOut);
  const [status, setStatus] = useState<LoginStatus>({ state: 'idle' });

  const login = useCallback(async () => {
    let runtime = detectAuthRuntime();
    let telegramInitData = '';

    if (runtime === 'web' && looksLikeTelegramRuntime()) {
      telegramInitData = await waitForTelegramInitData({ attempts: 4, delayMs: 150 });
      if (telegramInitData) {
        runtime = 'telegram';
      }
    }

    setRuntime(runtime);
    setStatus({ state: 'loading' });

    if (runtime === 'web') {
      try {
        const session = readStoredWebSession();
        if (!session?.accessToken) {
          setSignedOut('web');
          setStatus({ state: 'ready' });
          return;
        }

        const user = await restoreWebAppSession(session.accessToken);
        setAuth(session.accessToken, user, 'web');
      } catch {
        clearStoredWebSession();
        setSignedOut('web');
      }

      setStatus({ state: 'ready' });
      return;
    }

    try {
      await ensureTelegramWebAppSdk();
      const initData = telegramInitData || (await waitForTelegramInitData());
      if (!initData) {
        throw new Error('Telegram initData missing');
      }
      const result = await loginWithTelegram(initData);
      setAuth(result.accessToken, result.user, 'telegram');
      setStatus({ state: 'ready' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setStatus({ state: 'error', reason: 'telegram_auth_failed', message });
    }
  }, [setAuth, setRuntime, setSignedOut]);

  return { login, status };
}
